import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { useState } from "react";
import { EyeOff, Eye, X } from "lucide-react";
import { passwordRegex, userIdRegex } from "@/lib/regex";
import { usePostLogin } from "@/lib/api/auth/hooks";
import { createFieldNavigationHandler } from "@/lib/utils";
import type { ApiErrorResponse } from "@growing/types";

const STUDENT_TITLE = `Your English journey
starts here`;

const loginSchema = z
  .object({
    userId: z.string(),
    password: z.string(),
  })
  .superRefine((data, ctx) => {
    const isValidUserId = userIdRegex.test(data.userId);
    const isValidPassword = passwordRegex.test(data.password);

    const isInvalid = !isValidUserId || !isValidPassword;

    if (isInvalid) {
      ctx.addIssue({
        code: "custom",
        message: "아이디와 비밀번호를 확인해주세요.",
        path: ["userId"],
      });
    }

    if (isInvalid) {
      ctx.addIssue({
        code: "custom",
        message: "아이디와 비밀번호를 확인해주세요.",
        path: ["password"],
      });
    }
  });

type LoginFormValues = z.infer<typeof loginSchema>;

function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputType = showPassword ? "text" : "password";

  const navigate = useNavigate();

  const form = useForm<LoginFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(loginSchema),
    defaultValues: {
      userId: "",
      password: "",
    },
  });

  const loginMutation = usePostLogin();

  const handleSubmit = async (data: LoginFormValues) => {
    try {
      const result = await loginMutation.mutateAsync(data);

      // 쿠키에 accessToken이 자동으로 설정되므로, user 정보만 localStorage에 저장
      localStorage.setItem("growing-teacher-user", JSON.stringify(result.user));

      // 승인 여부에 따라 리다이렉트
      const redirectUrl = result.user.isApproved
        ? "/main"
        : "/pending-approval";

      navigate({ to: redirectUrl, replace: true });
    } catch (error: unknown) {
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message || "로그인에 실패했습니다";
      form.setError("userId", {
        message: errorMessage,
      });
      form.setError("password", {
        message: errorMessage,
      });
    }
  };

  const handleClickSignup = () => {
    navigate({ to: "/auth/signup/required-term" });
  };

  const handleClickFindId = () => {
    navigate({ to: "/auth/find-id" });
  };

  const handleClickFindPassword = () => {
    navigate({ to: "/auth/find-password" });
  };

  return (
    <main className="flex min-h-screen flex-col items-center px-6 pt-24 pb-10 gap-10">
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-bold break-keep whitespace-pre-wrap">
          {STUDENT_TITLE}
        </h1>
      </div>
      <div className="w-full max-w-md">
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="gap-10 flex flex-col"
        >
          <FieldGroup>
            <Controller
              name="userId"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel htmlFor="login-userId">아이디</FieldLabel>
                  <Input
                    {...field}
                    id="login-userId"
                    type="text"
                    placeholder="아이디를 입력해 주세요."
                    autoComplete="username"
                    enterKeyHint="next"
                    maxLength={20}
                    aria-invalid={fieldState.invalid}
                    onKeyDown={createFieldNavigationHandler(
                      form.setFocus,
                      "password"
                    )}
                    rightIcon={
                      field.value.length > 0 ? (
                        <X
                          onClick={() => field.onChange("")}
                          className="size-4 text-muted-foreground"
                        />
                      ) : null
                    }
                  />
                </Field>
              )}
            />

            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel htmlFor="login-password">비밀번호</FieldLabel>
                  <Input
                    {...field}
                    id="login-password"
                    type={passwordInputType}
                    placeholder="비밀번호를 입력하세요"
                    autoComplete="current-password"
                    enterKeyHint="done"
                    aria-invalid={fieldState.invalid}
                    rightIcon={
                      field.value.length > 0 ? (
                        showPassword ? (
                          <EyeOff
                            onClick={() => setShowPassword(false)}
                            className="size-4 text-muted-foreground"
                          />
                        ) : (
                          <Eye
                            onClick={() => setShowPassword(true)}
                            className="size-4 text-muted-foreground"
                          />
                        )
                      ) : null
                    }
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </FieldGroup>

          <div className="flex flex-col gap-3.5">
            <Button
              type="submit"
              className="w-full"
              disabled={!form.formState.isDirty || loginMutation.isPending}
            >
              {loginMutation.isPending ? "로그인 중..." : "로그인"}
            </Button>

            <Button
              type="button"
              className="w-full"
              variant="secondary"
              disabled={loginMutation.isPending}
              onClick={handleClickSignup}
            >
              회원가입
            </Button>

            <div className="flex flex-col gap-2">
              <div className="flex justify-center">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={handleClickFindId}
                >
                  아이디 찾기
                </Button>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={handleClickFindPassword}
                >
                  비밀번호 재설정
                </Button>
              </div>
              <span className="text-center text-sm text-muted-foreground">
                앱 버전 v1.0.0
              </span>
            </div>
          </div>
        </form>
      </div>
    </main>
  );
}

export const Route = createFileRoute("/auth/login")({
  component: LoginPage,
});
